/**
 * Tenant-admin Sales dashboard session, browser side. The signed Studio
 * token lives in an HttpOnly cookie the page cannot read
 * (lib/tenantAdminCookies.ts): the login, set-password and change-password
 * API routes set it, middleware.ts turns it into the proxies' bearer
 * header, and /api/studio/tenant-admin-logout clears it. The page only
 * sees when the session expires.
 *
 * Reads go through fetchWithTenantAdminAuth. The only writes are the
 * account's own Profile (name, password), through sendWithTenantAdminAuth.
 */
import { useEffect } from 'react'

import { TENANT_ADMIN_EXPIRY_COOKIE } from '@/lib/tenantAdminCookies'

// Where the token lived before it moved to a cookie; cleared on sign-out.
const LEGACY_TOKEN_KEY = 'studio_tenant_admin_token'

/** When this browser's session expires (epoch ms), or null when there is
 * no session or it has already expired. */
export function getTenantAdminSessionExpiry(): number | null {
	if (typeof document === 'undefined') return null
	const prefix = `${TENANT_ADMIN_EXPIRY_COOKIE}=`
	const raw = document.cookie
		.split('; ')
		.find(part => part.startsWith(prefix))
		?.slice(prefix.length)
	const expiresAt = Number(raw)
	return Number.isFinite(expiresAt) && expiresAt > Date.now() ? expiresAt : null
}

export function hasTenantAdminSession(): boolean {
	return getTenantAdminSessionExpiry() !== null
}

/** Signs this browser out: the readable expiry cookie at once, the HttpOnly
 * token cookie through the logout route. */
export function clearTenantAdminSession(): void {
	if (typeof document === 'undefined') return
	document.cookie = `${TENANT_ADMIN_EXPIRY_COOKIE}=; Path=/; Max-Age=0; SameSite=Lax`
	try {
		window.localStorage.removeItem(LEGACY_TOKEN_KEY)
	} catch {
		/* storage blocked */
	}
	fetch('/api/studio/tenant-admin-logout', {
		method: 'POST',
		keepalive: true,
	}).catch(() => {})
}

/** Calls `onExpired` when the session expires — while the page is open,
 * and on return to a tab whose timer was paused (sleep, background tab). */
export function useTenantAdminSessionTimeout(onExpired: () => void): void {
	useEffect(() => {
		const expiresAt = getTenantAdminSessionExpiry()
		if (expiresAt === null) return
		const check = () => {
			if (Date.now() >= expiresAt) onExpired()
		}
		const timer = window.setTimeout(check, expiresAt - Date.now())
		document.addEventListener('visibilitychange', check)
		return () => {
			window.clearTimeout(timer)
			document.removeEventListener('visibilitychange', check)
		}
	}, [onExpired])
}

/** 401: the token is missing, expired or revoked (a password change signs
 * out every other session). Callers clear it and go to the login page. */
export class TenantAdminSessionExpired extends Error {}

/** Any other non-2xx answer, with the status and the parsed JSON body so a
 * page can tell a 404 from a 429 and show field errors from a 400. */
export class TenantAdminRequestError extends Error {
	readonly status: number
	readonly body: Record<string, unknown>

	constructor(status: number, body: Record<string, unknown>, message: string) {
		super(message)
		this.status = status
		this.body = body
	}
}

/** The session cookie rides along on its own (same origin). */
async function send<T>(path: string, init: Parameters<typeof fetch>[1]): Promise<T> {
	if (!hasTenantAdminSession()) throw new TenantAdminSessionExpired('No session')
	const res = await fetch(path, { ...init, credentials: 'same-origin' })
	if (res.status === 401) {
		clearTenantAdminSession()
		throw new TenantAdminSessionExpired('Session expired')
	}
	if (!res.ok) {
		const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
		const detail = typeof body.detail === 'string' ? body.detail : ''
		throw new TenantAdminRequestError(
			res.status,
			body,
			detail || `${path} failed: ${res.status}`
		)
	}
	return (await res.json()) as T
}

/** A raw GET on the session, for a caller that needs the Response itself
 * (a file download). Throws TenantAdminSessionExpired like the others. */
export async function tenantAdminFetch(path: string): Promise<Response> {
	if (!hasTenantAdminSession()) throw new TenantAdminSessionExpired('No session')
	const res = await fetch(path, { credentials: 'same-origin' })
	if (res.status === 401) {
		clearTenantAdminSession()
		throw new TenantAdminSessionExpired('Session expired')
	}
	return res
}

/** GET a tenant-admin proxy on the session. */
export function fetchWithTenantAdminAuth<T>(path: string): Promise<T> {
	return send<T>(path, { method: 'GET' })
}

/** POST or PATCH a tenant-admin proxy with a JSON body. Same session
 * semantics as fetchWithTenantAdminAuth. */
export function sendWithTenantAdminAuth<T>(
	path: string,
	method: 'POST' | 'PATCH',
	body: unknown
): Promise<T> {
	return send<T>(path, {
		method,
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(body),
	})
}

/** GET a tenant-admin download proxy and hand the browser the file. The
 * filename is the one Studio set in Content-Disposition, else `fallbackName`. */
export async function downloadWithTenantAdminAuth(
	path: string,
	fallbackName: string
): Promise<void> {
	const res = await tenantAdminFetch(path)
	if (!res.ok) {
		const body = (await res.json().catch(() => ({}))) as Record<string, unknown>
		throw new TenantAdminRequestError(res.status, body, `${path} failed: ${res.status}`)
	}
	const disposition = res.headers.get('content-disposition') || ''
	const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
	const filename = match ? decodeURIComponent(match[1]) : fallbackName
	const url = URL.createObjectURL(await res.blob())
	const link = document.createElement('a')
	link.href = url
	link.download = filename
	document.body.appendChild(link)
	link.click()
	link.remove()
	// Revoke on the next tick: some browsers start the download lazily.
	setTimeout(() => URL.revokeObjectURL(url), 0)
}
